import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  CSSProperties,
  ChangeEvent,
  FocusEvent,
  KeyboardEvent,
  ReactElement,
  RefObject,
} from 'react';

import styles from './FadeInputField.module.css';

/**
 * Перенос `lib/src/presentation/widgets/fade_input_field.dart`.
 *
 * Во Flutter это `EditableText` с `maxLines: 10`, текстом по центру и белым
 * курсором; в вебе — `<textarea>`, который растёт по содержимому до тех же
 * десяти строк.
 */

/** maxLines во Flutter. */
const MAX_LINES = 10;

/**
 * Поле растёт по содержимому: высота сбрасывается и берётся из scrollHeight,
 * как `maxLines` у EditableText. Выше десяти строк включается прокрутка
 * (ограничение `max-height` в стилях).
 */
function useAutoGrow(ref: RefObject<HTMLTextAreaElement | null>, value: string): void {
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    node.style.height = 'auto';
    node.style.height = `${node.scrollHeight}px`;
  }, [ref, value]);
}

export interface FadeInputFieldProps {
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  inputRef?: RefObject<HTMLTextAreaElement | null>;
  /** Подсказка; видна, только пока поле пустое. */
  hintText?: string;
  autoFocus?: boolean;
  onEditingComplete?: (value: string) => void;
  onFieldSubmitted?: (value: string) => void;
  onFocusChanged?: (hasFocus: boolean, value: string) => void;
  className?: string;
}

export function FadeInputField(props: FadeInputFieldProps): ReactElement {
  const {
    value, defaultValue, onChange, inputRef, hintText, autoFocus = true,
    onEditingComplete, onFieldSubmitted, onFocusChanged, className,
  } = props;

  const innerRef = useRef<HTMLTextAreaElement | null>(null);
  const [freeValue, setFreeValue] = useState(defaultValue ?? '');
  const currentValue = value ?? freeValue;
  // Пусто ли поле — от этого зависит, показывать ли подсказку.
  const isEmpty = currentValue.length === 0;

  // Единственная разрешённая мутация — `ref.current`: так React устроен.
  const attachInput = useCallback(
    (node: HTMLTextAreaElement | null): void => {
      innerRef.current = node;
      if (inputRef) inputRef.current = node;
    },
    [inputRef],
  );
  useAutoGrow(innerRef, currentValue);

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    const next = event.target.value;
    if (value === undefined) setFreeValue(next);
    onChange?.(next);
  };
  const handleFocus = (hasFocus: boolean) => (event: FocusEvent<HTMLTextAreaElement>): void =>
    onFocusChanged?.(hasFocus, event.target.value);

  /*
    Во Flutter поле многострочное (`maxLines: 10`), и Enter вставлял перенос
    строки — отправляли кнопка и потеря фокуса. На клавиатуре браузера Enter
    привычнее как «отправить», поэтому он отправляет; перенос строки остался
    на Shift+Enter.

    Колбэк вызывается РОВНО ОДИН. `EditableText._finalizeEditing` дёргает и
    `onEditingComplete`, и `onSubmitted`, но до него Enter в многострочном
    поле не доходил вовсе. Здесь доходит, а `ChatKeyboardMode` передаёт в оба
    колбэка одну и ту же функцию — вызвав оба, мы отправляли вопрос дважды.
    Второй заход не добавлял сообщение (его гасит `fetchingResponse`), зато
    открывал второй поток с идентификатором пузыря, которого нет в ленте, —
    и ответ модели уходил в никуда.
  */
  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    const text = event.currentTarget.value;
    const submit = onFieldSubmitted ?? onEditingComplete;
    submit?.(text);
  };

  const valueProps = value === undefined ? { defaultValue } : { value };

  return (
    <div
      className={[styles.root, className].filter(Boolean).join(' ')}
      style={{ '--fade-max-lines': MAX_LINES } as CSSProperties}
    >
      <div className={styles.stack}>
        {hintText !== undefined && isEmpty && (
          <div className={styles.hint}>{hintText}</div>
        )}
        <textarea
          {...valueProps}
          ref={attachInput}
          className={styles.input}
          rows={1}
          autoFocus={autoFocus}
          onChange={handleChange}
          onFocus={handleFocus(true)}
          onBlur={handleFocus(false)}
          onKeyDown={handleKeyDown}
        />
      </div>
    </div>
  );
}
